/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
import * as zrender from 'zrender';
import { ElementEvent } from 'zrender/src/Element';
import GlobalModel from 'echarts/lib/model/Global';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import SeriesData from 'echarts/lib/data/SeriesData';
import SymbolClz from 'echarts/lib/chart/helper/Symbol';
import type Model from 'echarts/lib/model/Model';
import { type ScatterSeriesOption } from 'echarts/lib/chart/scatter/ScatterSeries';
import { type SymbolDrawSeriesScope } from 'echarts/lib/chart/helper/SymbolDraw';
import { getLabelStatesModels } from 'echarts/lib/label/labelStyle';
import WordCloudSeries, {
  WordCloudBaseLayoutOption,
  WordCloudDataItemOption
} from '../WordCloudSeries';
import { type Point } from '../type';
import { shuffleArray } from './helper';
import { formatPaddingOrMargin } from '../../../../helper/wordCloud';

export default function normalLayout(ecModel: GlobalModel, api: ExtensionAPI) {
  // eslint-disable-next-line func-names, @typescript-eslint/no-misused-promises
  ecModel.eachSeriesByType('wordCloud', function (wordCloudSeries: WordCloudSeries) {
    // 正常布局不依赖坐标系
    // TODO:判定条件目前并不严谨
    const coordSys = wordCloudSeries.coordinateSystem;
    if (coordSys) {
      return;
    }

    doNormalLayout(wordCloudSeries, api);
  });
}

function doNormalLayout(wordCloudSeries: WordCloudSeries, api: ExtensionAPI) {
  const layoutConfig = wordCloudSeries.get('layout');
  const { precision, mask, overlapAreaThreshold, isDebug } = layoutConfig;

  // step0 构建指定精度的画布矩阵
  const grid = createdGrid(precision, mask, api);

  // step2 格式化数据，获取单个词所在图形的占用矩阵
  /**
   * 后续如果想支持其他形状
   * 1.需要用ECharts扩展对应的图形
   * 2.需要针对对应的图形做碰撞检测，与外接矩形形状填充率在90%以上的情况，仍可以使用symbolSize
   * 填充率低的情况：
   * 2.1.直角三角形：50%
   * 2.2.圆形：πr^2/(2r)^2 = π/4 = 78.5%
   *
   * 像素分布信息方案：
   * 获取图形的外接矩形作为基础矩形，内存中，在该矩形内绘制一个填充为[非白色]的图形，根据像素是否为[非白色]判断有无占用，最后得到单个图形的像素占用二维矩阵
   * 循环可以放置的点，判断是否有足够的空间放置图形（重叠像素点小于阈值）
   */
  const formatWordCloudData = handleFormatData(wordCloudSeries);

  // step3 获取点集，即矩形可能放置的位置，点的位置与矩形的中心点重合
  const points = getPointsAtRadius(grid);

  // step4 尝试放置图形
  const seriesData = wordCloudSeries.getData();
  for (let i = 0; i < formatWordCloudData.length; i += 1) {
    const { occupyGrid, index } = formatWordCloudData[i];
    let hasPointToPut = false;
    for (let j = 0; j < points.length; j += 1) {
      const point = points[j];
      hasPointToPut = canPutWord(occupyGrid, grid, point, overlapAreaThreshold, isDebug);
      if (hasPointToPut) {
        seriesData.setItemLayout(index, [point[0] * precision, point[1] * precision]);
        break;
      }
    }
    // 如果没有可以放置的位置，则赋值为NaN
    if (!hasPointToPut) {
      seriesData.setItemLayout(index, [NaN, NaN]);
    }
  }
}

function createdGrid(
  precision: number,
  mask: WordCloudBaseLayoutOption['mask'],
  api: ExtensionAPI
) {
  const rect = api.getDom().getBoundingClientRect();
  const { width, height } = rect;
  const gridWidth = Math.floor(width / precision);
  const gridHeight = Math.floor(height / precision);
  // 创建二维矩阵，初始化为true，占用后标记为false，后续判定时可以兼容越界，即单元格不存在的情况
  const result: boolean[][] = [];
  for (let i = 0; i < gridHeight; i += 1) {
    result[i] = [];
    for (let j = 0; j < gridWidth; j += 1) {
      result[i][j] = true;
    }
  }

  if (mask && 'imageData' in mask) {
    const canvasDom = document.createElement('canvas');
    canvasDom.id = 'mask';
    canvasDom.width = gridWidth;
    canvasDom.height = gridHeight;
    const ctx = canvasDom.getContext('2d');
    if (ctx) {
      initGridWithMask(ctx, result, mask.imageData);
    }
  }
  return result;
}

function initGridWithMask(
  ctx: CanvasRenderingContext2D,
  grid: boolean[][],
  image: HTMLImageElement
): void {
  const gridWidth = grid[0].length;
  const gridHeight = grid.length;
  ctx.drawImage(image, 0, 0, gridWidth, gridHeight);
  const imageData = ctx.getImageData(0, 0, gridWidth, gridHeight);
  for (let i = 0; i < gridHeight; i += 1) {
    for (let j = 0; j < gridWidth; j += 1) {
      // white means empty
      const R = imageData.data[(i * gridWidth + j) * 4];
      const G = imageData.data[(i * gridWidth + j) * 4 + 1];
      const B = imageData.data[(i * gridWidth + j) * 4 + 2];
      const A = imageData.data[(i * gridWidth + j) * 4 + 3];
      if (R !== 255 || G !== 255 || B !== 255 || A !== 255) {
        grid[i][j] = false;
      }
    }
  }
}

function handleFormatData(wordCloudSeries: WordCloudSeries) {
  const { option } = wordCloudSeries;
  return (
    (option.data as WordCloudDataItemOption[])
      .map((item, index) => ({
        ...item,
        occupyGrid: getOccupyGrid(item, index, wordCloudSeries),
        index
      }))
      // @ts-ignore
      .sort((a, b) => b.value[1] - a.value[1])
  );
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
function getOccupyGrid(
  item: WordCloudDataItemOption,
  index: number,
  wordCloudSeries: WordCloudSeries
) {
  const layoutConfig = wordCloudSeries.get('layout');
  const { precision, isLayoutByText, isDebug } = layoutConfig;
  const { symbolSize, symbol, itemStyle, value } = item;
  const { margin = 0 } = itemStyle;
  const formattedMargin = formatPaddingOrMargin(margin);
  const symbolTemp = symbol || wordCloudSeries.get('symbol');
  const hasMargin = formattedMargin.some(d => d !== 0);
  const isRectLike = symbolTemp === 'rect' && !isLayoutByText;

  // 判断是否有额外icon布局
  if (value.length > 2) {
    symbolSize[0] += value[2].style.width;
  }
  /**
   * 是否直接使用外接矩形布局
   * 1.如果margin不为0，那么直接使用外接矩形布局
   * 2.如果是文本布局，且为填充率高的图形，则使用外接矩形布局
   */
  const useCircRect = hasMargin || isRectLike;
  const formatWidth = Math.ceil(
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    (symbolSize[0] + formattedMargin[1] + formattedMargin[3]) / precision
  );
  const formatHeight = Math.ceil(
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    (symbolSize[1] + formattedMargin[0] + formattedMargin[2]) / precision
  );
  /**
   * false表示占用，true表示未占用
   */
  const occupyGrid: boolean[][] = [];
  for (let i = 0; i < formatHeight; i += 1) {
    occupyGrid[i] = [];
    for (let j = 0; j < formatWidth; j += 1) {
      // 如果使用外接矩形计算occupy，则直接赋值并返回，即默认矩形内全占用
      if (useCircRect) {
        occupyGrid[i][j] = false;
      } else {
        occupyGrid[i][j] = true;
      }
    }
  }

  if (useCircRect) {
    if (isDebug) {
      drawOccupyGrid(occupyGrid, formatWidth, formatHeight);
    }
    return occupyGrid;
  }

  // 创建ZRender实例，绘制symbol
  const seriesData = wordCloudSeries.getData();
  // 此时ecahrt还没有赋值visual，如果用户没有给初始颜色会导致下面的渲染出问题，这里给一个默认色
  seriesData.setItemVisual(index, 'style', {
    fill: isLayoutByText ? '#fff' : '#000',
    stroke: isLayoutByText ? '#fff' : '#000'
  });
  seriesData.setItemVisual(index, 'symbol', symbolTemp as string);
  seriesData.setItemVisual(
    index,
    'symbolSize',
    (symbolSize as number[]).map(d => d / precision)
  );
  const seriesScope = makeSeriesScope(seriesData);
  const symbolIns = new SymbolClz(seriesData, index, seriesScope, {
    disableAnimation: true
  });
  const symbolObj = symbolIns.childAt(0);
  symbolObj.attr({
    // ecahrts内部会给一个层级，导致label可能被图形覆盖
    // @ts-ignore
    z2: 0
  });
  // 按照精度缩小字号
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  symbolObj._textContent.attr({
    scaleX: 1 / precision,
    scaleY: 1 / precision
  });

  // 绘制完毕后清除影响
  // eslint-disable-next-line no-undefined
  seriesData.setItemVisual(index, 'style', undefined);

  // TODO:并不是每个图形的中心都是position，有些图形，比如pin，position是在图形的底部，这里需要根据图形的形状来计算中心点，以保证图形一定在自己矩阵的中心位置
  symbolIns.setPosition([formatWidth / 2, formatHeight / 2]);
  // @ts-ignore
  symbolIns.drift = drift;

  const zRenderContainer = document.createElement('div');
  zRenderContainer.id = `zrender${index}`;
  zRenderContainer.style.width = `${formatWidth}px`;
  zRenderContainer.style.height = `${formatHeight}px`;
  document.body.appendChild(zRenderContainer);

  const zr = zrender.init(zRenderContainer, {
    width: formatWidth,
    height: formatHeight,
    devicePixelRatio: 1
  });
  // @ts-ignore
  zr.add(symbolIns);
  zr.refreshImmediately();

  const canvas = zRenderContainer.querySelector('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const imageData = ctx.getImageData(0, 0, formatWidth, formatHeight);
    for (let y = 0; y < formatHeight; y += 1) {
      for (let x = 0; x < formatWidth; x += 1) {
        const R = imageData.data[(y * formatWidth + x) * 4];
        const G = imageData.data[(y * formatWidth + x) * 4 + 1];
        const B = imageData.data[(y * formatWidth + x) * 4 + 2];
        const A = imageData.data[(y * formatWidth + x) * 4 + 3];
        // 对边缘做抗锯齿处理
        const isBoundaryPixels =
          y === 0 || y === formatHeight - 1 || x === 0 || x === formatWidth - 1;
        // eslint-disable-next-line thsjs/expression-complexity
        const isOccupied = isBoundaryPixels
          ? R < 128 || G < 128 || B < 128 || A < 128
          : R !== 255 || G !== 255 || B !== 255 || A !== 255;
        // eslint-disable-next-line max-depth
        if (isOccupied) {
          occupyGrid[y][x] = false;
        }
      }
    }
  }

  if (isDebug) {
    drawOccupyGrid(occupyGrid, formatWidth, formatHeight);
  } else {
    document.body.removeChild(zRenderContainer);
  }

  return occupyGrid;
}

function drawOccupyGrid(occupyGrid: boolean[][], formatWidth: number, formatHeight: number) {
  // 开启调试模式后，绘制occupyGrid以验证是否计算正确
  // 创建一个宽度为 W，高度为 H 的 canvas
  const debugContainer = document.createElement('div');
  const occupyCanvas = document.createElement('canvas');
  occupyCanvas.width = formatWidth;
  occupyCanvas.height = formatHeight;
  const occupyCtx = occupyCanvas.getContext('2d');

  // 遍历二维布尔矩阵，如果值为 false，则将对应的像素点绘制为黑色
  for (let y = 0; y < formatHeight; y += 1) {
    for (let x = 0; x < formatWidth; x += 1) {
      // eslint-disable-next-line max-depth
      if (!occupyGrid[y][x]) {
        occupyCtx.fillStyle = 'black';
        occupyCtx.fillRect(x, y, 1, 1);
      }
    }
  }

  // 将 canvas 添加到页面中
  debugContainer.appendChild(occupyCanvas);
  document.body.appendChild(debugContainer);
}

/**
 * 这里直接copy的源码
 * @see https://github.com/apache/echarts/blob/master/src/chart/helper/SymbolDraw.ts#L136
 */
function makeSeriesScope(data: SeriesData): SymbolDrawSeriesScope {
  const seriesModel = data.hostModel as Model<ScatterSeriesOption>;
  const emphasisModel = seriesModel.getModel('emphasis');
  return {
    emphasisItemStyle: emphasisModel.getModel('itemStyle').getItemStyle(),
    blurItemStyle: seriesModel.getModel(['blur', 'itemStyle']).getItemStyle(),
    selectItemStyle: seriesModel.getModel(['select', 'itemStyle']).getItemStyle(),
    focus: emphasisModel.get('focus'),
    blurScope: emphasisModel.get('blurScope'),
    emphasisDisabled: emphasisModel.get('disabled'),
    hoverScale: emphasisModel.get('scale'),
    labelStatesModels: getLabelStatesModels(seriesModel),
    cursorStyle: seriesModel.get('cursor')
  };
}

/**
 * ECharts内部对该方法做了重写，需要还原
 * @see https://github.com/apache/echarts/blob/master/src/chart/helper/Symbol.ts#L89
 * @see https://github.com/ecomfe/zrender/blob/master/src/Element.ts#L450
 * Drift element
 * @param {number} dx dx on the global space
 * @param {number} dy dy on the global space
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
function drift(dx: number, dy: number, e?: ElementEvent) {
  // @ts-ignore
  // eslint-disable-next-line default-case, @typescript-eslint/no-unsafe-member-access, no-invalid-this
  switch (this.draggable) {
    case 'horizontal':
      dy = 0;
      break;
    case 'vertical':
      dx = 0;
      break;
  }
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, no-invalid-this
  let m = this.transform;
  if (!m) {
    // @ts-ignore
    // eslint-disable-next-line no-multi-assign, @typescript-eslint/no-unsafe-member-access, no-invalid-this
    m = this.transform = [1, 0, 0, 1, 0, 0];
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  m[4] += dx;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  m[5] += dy;

  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, no-invalid-this
  this.decomposeTransform();
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, no-invalid-this
  this.markRedraw();
}

function getPointsAtRadius(grid: boolean[][]) {
  const gridWidth = grid[0].length;
  const gridHeight = grid.length;

  // eslint-disable-next-line no-magic-numbers
  const center: [number, number] = [Math.floor(gridWidth / 2), Math.floor(gridHeight / 2)];

  // eslint-disable-next-line no-magic-numbers
  const maxRadius = Math.max(Math.ceil(gridWidth / 2), Math.ceil(gridHeight / 2));

  /**
   * 从中心点开始，从0~maxRadius，逐渐扩大半径n，每次递增1格的单位，获取半径所在圆上的网格点，从3点方向开始,逆时针取8n个点（即角度从0度取到360度）
   */
  const points: [number, number][] = [];

  for (let r = 0; r < maxRadius; r += 1) {
    const circlePoints = getCirclePoints(center, r, { gridWidth, gridHeight });
    points.push(...shuffleArray(circlePoints));
  }

  return points;
}

function getCirclePoints(
  center: [number, number],
  r: number,
  config: { gridWidth: number; gridHeight: number }
) {
  const { gridWidth, gridHeight } = config;
  const gridSizeMin = Math.min(gridWidth, gridHeight);
  const gridSizeMax = Math.max(gridWidth, gridHeight);
  const percent = r / gridSizeMax;
  if (r === 0) {
    return [center];
  }

  /**
   * 当r的值逐渐增大时，在对应半径上的划分会越来越密集，需要设置一个临界点
   * 设定为：目前r对应的小矩形的周长/2
   */
  // eslint-disable-next-line no-magic-numbers
  const MAX_COUNT = Math.floor((gridSizeMin + gridSizeMax) * percent) / 2;

  // 分割数
  // eslint-disable-next-line no-magic-numbers
  const splitNumber = Math.min(r * 8, MAX_COUNT);

  // 每次递增的弧度
  // eslint-disable-next-line no-magic-numbers
  const stepArc = (Math.PI * 2) / splitNumber;

  const points: [number, number][] = [];
  // eslint-disable-next-line no-magic-numbers
  for (let i = 0; i < splitNumber; i += 1) {
    const x = center[0] + r * Math.cos(i * stepArc);
    const y = center[1] + r * Math.sin(i * stepArc);
    // 临界判断
    if (x >= 0 && x <= gridWidth && y >= 0 && y <= gridHeight) {
      points.push([Math.floor(x), Math.floor(y)]);
    }
  }

  // 去重后返回
  return Array.from(new Set(points));
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
function canPutWord(
  occupyGrid: boolean[][],
  grid: boolean[][],
  point: Point,
  overlapAreaThreshold = 0,
  isDebug = false
) {
  const [x, y] = point;
  const formatHeight = occupyGrid.length;
  const formatWidth = occupyGrid[0].length;
  const gridHeight = grid.length;
  const gridWidth = grid[0].length;

  // 临界判断
  if (
    x - formatWidth / 2 < 0 ||
    x + formatWidth / 2 > gridWidth ||
    y - formatHeight / 2 < 0 ||
    y + formatHeight / 2 > gridHeight
  ) {
    return false;
  }

  let overlapAreaCount = 0;
  for (let i = 0; i < formatHeight; i += 1) {
    for (let j = 0; j < formatWidth; j += 1) {
      const gridX = x + j - Math.ceil(formatWidth / 2);
      const gridY = y + i - Math.ceil(formatHeight / 2);
      if (!occupyGrid[i][j] && grid[gridY] && !grid[gridY][gridX]) {
        overlapAreaCount += 1;
        // eslint-disable-next-line max-depth
        if (overlapAreaCount > overlapAreaThreshold) {
          return false;
        }
      }
    }
  }

  // 如果可以放置，则更新grid
  for (let i = 0; i < formatHeight; i += 1) {
    for (let j = 0; j < formatWidth; j += 1) {
      const gridX = x + j - Math.ceil(formatWidth / 2);
      const gridY = y + i - Math.ceil(formatHeight / 2);
      // 如果图形增加了padding，则有可能超出grid，在这里做极值判断
      if (!occupyGrid[i][j] && grid[gridY] && grid[gridY][gridX]) {
        grid[gridY][gridX] = false;
      }
    }
  }

  if (isDebug) {
    drawGrid(grid);
  }

  return true;
}

function drawGrid(grid: boolean[][]) {
  const gridWidth = grid[0].length;
  const gridHeight = grid.length;
  // 创建一个宽度为 W，高度为 H 的 canvas
  const debugContainer = document.createElement('div');
  const occupyCanvas = document.createElement('canvas');
  occupyCanvas.width = gridWidth;
  occupyCanvas.height = gridHeight;
  const occupyCtx = occupyCanvas.getContext('2d');

  // 遍历二维布尔矩阵，如果值为 false，则将对应的像素点绘制为黑色
  for (let y = 0; y < gridHeight; y += 1) {
    for (let x = 0; x < gridWidth; x += 1) {
      // eslint-disable-next-line max-depth
      if (!grid[y][x]) {
        occupyCtx.fillStyle = 'black';
        occupyCtx.fillRect(x, y, 1, 1);
      }
    }
  }

  // 将 canvas 添加到页面中
  debugContainer.appendChild(occupyCanvas);
  document.body.appendChild(debugContainer);
}
